import type { ExpDisplayUpdate, ExpReadoutSnapshot, ExpRecognitionIssue, ExpRecognitionProgress, ValidatedObservation } from '@/domain/types'

/** Retains the latest unresolved reason while the next task queues/runs. */
export class ExpRecognitionProgressTracker {
  private scope: string | null = null
  private value: ExpRecognitionProgress = this.empty()

  observe(scope: string, observation: ValidatedObservation, display?: ExpDisplayUpdate, now = Date.now(), readout?: ExpReadoutSnapshot): ExpRecognitionProgress {
    if (scope !== this.scope) { this.scope = scope; this.value = this.empty() }
    const d = observation.diagnostics.metrics?.exp, completion = d?.completion
    if (completion && completion.capturedAt >= (this.value.lastCompletedSourceAt ?? -Infinity)) {
      this.value = { ...this.value, lastCompletedAt: completion.completedAt ?? null, lastCompletedSourceAt: completion.capturedAt }
      if (!['ready', 'disabled', 'source-hidden', 'queued', 'running'].includes(completion.status)) {
        this.issue({ kind: 'ocr', reason: completion.failureReason ?? (['no-text', 'parse-failed', 'low-confidence', 'confirming'].includes(completion.status)
          ? completion.status as 'no-text' | 'parse-failed' | 'low-confidence' | 'confirming' : 'no-result'),
          sourceAt: completion.capturedAt, observedAt: completion.completedAt ?? now })
      }
    }
    const decisions = [
      { sourceAt: observation.expExactCandidate?.sourceAt, admission: observation.expCandidateAdmission },
      { sourceAt: observation.expExactEvidence?.sourceAt, admission: observation.expEvidenceAdmission },
      { sourceAt: observation.expPercentCandidate?.sourceAt, admission: observation.expEstimatedAdmission },
    ].sort((a, b) => (a.sourceAt ?? -Infinity) - (b.sourceAt ?? -Infinity))
    for (const { sourceAt, admission } of decisions) {
      if (sourceAt !== undefined && admission && (admission.displayStatus ?? admission.status) !== 'accepted' && admission.reason !== 'stale') {
        this.issue({ kind: 'statistics', reason: admission.reason ?? 'pending', sourceAt, observedAt: now,
          ...(admission.invalidReason ? { invalidReason: admission.invalidReason } : {}),
          ...(admission.requirementConflict ? { requirementConflict: structuredClone(admission.requirementConflict) } : {}) })
      }
    }
    // Only a published, trusted endpoint clears the failure; successful OCR
    // alone may still be waiting for statistical confirmation.
    // An incomplete whole-session total can coexist with a trusted recovery
    // interval. Its published readout is also a successful statistics adoption.
    const percentageAccepted = observation.expEstimatedAdmission?.status === 'accepted'
      && observation.expEstimatedEvidence?.value.absolute === null ? observation.expEstimatedEvidence.sourceAt : null
    const acceptedAt = percentageAccepted ?? (readout?.scope === 'recovery' && readout.status === 'ready'
      ? readout.lastConfirmed?.sourceAt
      : display?.status === 'ready' && display.netGain !== null ? display.sourceAt : null)
    if (acceptedAt != null) {
      const at = Math.max(this.value.lastAcceptedSourceAt ?? -Infinity, acceptedAt)
      this.value = { ...this.value, lastAcceptedSourceAt: at,
        lastIssue: this.value.lastIssue && this.value.lastIssue.sourceAt > at ? this.value.lastIssue : null }
    } else if (!display && observation.accepted.exp && this.value.lastIssue
      && observation.exp.capturedAt >= this.value.lastIssue.sourceAt) {
      // Preview has no session statistics, but a newly accepted reading still
      // resolves its previous OCR failure without inventing a statistics time.
      this.value = { ...this.value, lastIssue: null }
    }
    return this.snapshot()
  }

  private issue(issue: ExpRecognitionIssue): void {
    if (issue.sourceAt <= (this.value.lastAcceptedSourceAt ?? -Infinity)) return
    const previous = this.value.lastIssue
    if (previous && issue.sourceAt < previous.sourceAt) return
    if (previous && issue.sourceAt === previous.sourceAt && issue.kind === previous.kind && issue.reason === previous.reason
      && !(issue.kind === 'statistics' && previous.kind === 'statistics' && issue.invalidReason !== previous.invalidReason)
      && !(issue.kind === 'statistics' && issue.requirementConflict)) return
    this.value = { ...this.value, lastIssue: issue }
  }

  snapshot(): ExpRecognitionProgress {
    return structuredClone(this.value)
  }

  private empty(): ExpRecognitionProgress {
    return { lastCompletedAt: null, lastCompletedSourceAt: null, lastAcceptedSourceAt: null, lastIssue: null }
  }
}

/** A stale MXDC read needs one bounded recheck, not a new accounting baseline. */
export function needsExpRecheck(readout: ExpReadoutSnapshot, presetId: string): boolean {
  return readout.status !== 'ready' && (readout.reason !== 'source-unreadable'
    || presetId === 'mxdc' && readout.status === 'stale')
}
